import { IconCamera, IconHeart, IconMessageCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fragments';
import { UserAvatar } from '@components/Community';

import { publishedLabel } from '../../postDate';

import './styles.scss';

const COLUMNS = 3;

function heartCount(post) {
    return Number(post?.reactions?.heart?.amount ?? post?.insights?.reactions ?? 0) || 0;
}

function commentCount(post) {
    return Number(post?.insights?.comments ?? 0) || 0;
}

// Round-robin into fixed columns: reading order stays left→right, top→bottom
// and each column stacks at natural heights (masonry without measuring).
function toColumns(posts) {
    const columns = Array.from({ length: COLUMNS }, () => []);
    posts.forEach((post, i) => columns[i % COLUMNS].push({ post, index: i }));
    return columns;
}

function activate(e, fn) {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fn();
    }
}

/**
 * PostsGrid — three masonry columns. Image posts keep their natural aspect
 * ratio; hover reveals the counts on an inverse scrim (ink-colored, so it
 * flips with the theme like the `inverse` button). Text posts are a soft
 * elevation card: publisher + date on top, the text, and the counts below.
 */
export default function PostsGrid({ posts, isOwn, ownerName, onOpen }) {
    const { t } = useTranslation('member');

    if (!posts.length) {
        return (
            <div className="posts-grid__empty">
                <span className="posts-grid__empty-icon"><IconCamera /></span>
                <Text.h5 weight={500} contrast={10}>{t('publicProfile.posts.emptyTitle')}</Text.h5>
                <Text.span contrast={6}>
                    {isOwn
                        ? t('publicProfile.posts.emptySubtitleOwn')
                        : t('publicProfile.posts.emptySubtitle', { name: ownerName })}
                </Text.span>
            </div>
        );
    }

    return (
        <div className="posts-grid">
            {toColumns(posts).map((column, c) => (
                <div className="posts-grid__column" key={c}>
                    {column.map(({ post, index }) => (
                        post.image
                            ? <ImageTile key={post.id || index} post={post} index={index} onOpen={onOpen} t={t} />
                            : <TextTile key={post.id || index} post={post} index={index} onOpen={onOpen} t={t} />
                    ))}
                </div>
            ))}
        </div>
    );
}

function ImageTile({ post, index, onOpen, t }) {
    const ratio = Number(post?.info?.photoAspectRatio) || undefined;
    const open = () => onOpen(post);

    return (
        <div
            role="button"
            tabIndex={0}
            className="posts-grid__tile posts-grid__tile--image"
            style={{ animationDelay: `${Math.min(index, 11) * 30}ms`, aspectRatio: ratio }}
            onClick={open}
            onKeyDown={(e) => activate(e, open)}
            aria-label={t('publicProfile.posts.openLabel')}
        >
            <img src={post.image} alt="" loading="lazy" />
        </div>
    );
}

function TextTile({ post, index, onOpen, t }) {
    const publisher = post.publisher || null;
    const open = () => onOpen(post);

    return (
        <div
            role="button"
            tabIndex={0}
            className="posts-grid__tile posts-grid__tile--text"
            style={{ animationDelay: `${Math.min(index, 11) * 30}ms` }}
            onClick={open}
            onKeyDown={(e) => activate(e, open)}
            aria-label={t('publicProfile.posts.openLabel')}
        >
            <div className="posts-grid__text-body">
                {publisher && (
                    <div className="posts-grid__publisher">
                        <UserAvatar user={publisher} size={24} link={false} />
                        <div className="posts-grid__publisher-copy">
                            <Text.label contrast={9} maxLines={1}>{publisher.username}</Text.label>
                            <Text.caption contrast={6}>{publishedLabel(post.timestamps?.published)}</Text.caption>
                        </div>
                    </div>
                )}

                <Text.p contrast={8} className="posts-grid__text">{post.text}</Text.p>

                <div className="posts-grid__meta">
                    <Text.caption contrast={6} left={<IconHeart />}>{heartCount(post)}</Text.caption>
                    <Text.caption contrast={6} left={<IconMessageCircle />}>{commentCount(post)}</Text.caption>
                </div>
            </div>
        </div>
    );
}
